import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { ErrorWithMessage, ReduxStoreRoot, TerminalSortResponse } from "../../types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Center,
  Title,
  Text,
  Loading,
  Cluster,
  ToastNotification,
  hooks,
} from "@thecb/components";
import Terminal from "../../types/api/models/terminal";
import { TableWrapper } from "../../components/styled";
import {
  Table,
  TableBody,
  TableHeader,
  TableHeading,
  TableRow,
  TableRowEntry,
} from "../../components/table";
import { useHistory } from "react-router-dom";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  terminalsTableReducer,
  terminalsTableMapStateToProps,
  terminalsTableMapDispatchToProps,
} from "./TerminalsTable.state";
import { setTerminals } from "./Terminals.state";
import DragHandle from "../../components/drag-handle";
import { arraysHaveSameOrder, fetchFunction } from "../../util";
import { RequestMethod } from "../../types/api/RequestMethod";
import { AlertVariants } from "../../types/Alert";

const tableColumns = ["Terminal", "Kiosk ID", "Location"];

const TerminalsTable = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { isToastOpen, toastVariant, toastMessage, showToast, hideToast } =
    hooks.useToastNotification({ timeout: 3000 });
  const { posApiEndpoint } = useSelector((state: ReduxStoreRoot) => state.config);
  const { terminals, terminalsCache } = useSelector(
    (state: ReduxStoreRoot) => state.posConfigTool.terminals
  );
  const terminalIds = terminals.map((terminal) => terminal.id);
  const { mutate: saveTerminalSortOrders } = useMutation<
    TerminalSortResponse,
    ErrorWithMessage,
    { terminal_ids: number[] }
  >({
    mutationFn: (body: { terminal_ids: number[] }) =>
      fetchFunction(`${posApiEndpoint}/terminals/sort`, {
        method: RequestMethod.PUT,
        body,
      }),
    onSuccess: () => {
      showToast({
        message: "Sort Order Updated",
        variant: AlertVariants.SUCCESS,
      });
    },
    onError: () => {
      dispatch(setTerminals(terminalsCache));
      showToast({
        message: "Error Updating Sort Order",
        variant: AlertVariants.ERROR,
      });
    },
  });
  const onRowClick = (terminal: Terminal) =>
    history.push(`${history.location.pathname}/${terminal.id}/edit`);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    const oldIndex = terminals.findIndex((terminal) => terminal.id === active.id);
    const newIndex = terminals.findIndex((terminal) => terminal.id === over?.id);

    const newTerminalsList = arrayMove(terminals, oldIndex, newIndex);
    const newTerminalIds = newTerminalsList.map((terminal: Terminal) => terminal.id);

    if (!arraysHaveSameOrder(terminalIds, newTerminalIds)) {
      dispatch(setTerminals(newTerminalsList));
      saveTerminalSortOrders({ terminal_ids: newTerminalIds });
    }
  };

  interface Props {
    terminal: Terminal;
  }
  const SortableRow = ({ terminal }: Props) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
    } = useSortable({ id: terminal.id });
    const style = {
      transition,
      transform: CSS.Transform.toString(transform),
    };
    return (
      <TableRow
        onClick={() => onRowClick(terminal)}
        extraStyles={`
          transition: ${style.transition}; 
          transform: ${style.transform}
        `}
        setNodeRef={setNodeRef}
      >
        <TableRowEntry>{terminal.name}</TableRowEntry>
        <TableRowEntry>{terminal.kiosk_id}</TableRowEntry>
        <TableRowEntry>
          <Cluster justify="space-between">
            {terminal.location}
            <DragHandle {...attributes} {...listeners} ref={setActivatorNodeRef} />
          </Cluster>
        </TableRowEntry>
      </TableRow>
    );
  };

  const queryClient = useQueryClient();

  if (queryClient.isFetching()) {
    return <Loading />;
  }

  return (
    <TableWrapper>
      <ToastNotification
        variant={toastVariant}
        message={toastMessage}
        toastOpen={isToastOpen}
        closeToastNotification={() => hideToast()}
      />
      <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <Table>
          <TableHeader>
            {tableColumns.map((c, idx) => (
              <TableHeading extraStyles={`text-align: left !important;`} key={idx}>
                {c}
              </TableHeading>
            ))}
          </TableHeader>
          <SortableContext
            items={terminalIds}
            strategy={verticalListSortingStrategy}
          >
            <TableBody>
              {terminals.map((terminal) => (
                <SortableRow key={terminal.id} terminal={terminal} />
              ))}
            </TableBody>
          </SortableContext>
        </Table>
      </DndContext>
      {!terminals.length && (
        <Box padding="64px" minWidth="100%">
          <Center intrinsic>
            <Title as="h2" weight="700" margin="0 0 0.25rem">
              No Terminals
            </Title>
            <Text>
              This client has no terminals. Create a terminal to see them listed
              here.
            </Text>
          </Center>
        </Box>
      )}
    </TableWrapper>
  );
};

TerminalsTable.reducer = terminalsTableReducer;
TerminalsTable.mapStateToProps = terminalsTableMapStateToProps;
TerminalsTable.mapDispatchToProps = terminalsTableMapDispatchToProps;

export default TerminalsTable;
